import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { ScrollArea } from "../../../../../components/ui/scroll-area";
import { Card, CardContent } from "../../../../../components/ui/card";
import { type AuditSession } from "../../../../../models/audit/auditSession";
import {
  type VideoDocument,
  type VideoKeyMoment,
  type VideoChapter,
} from "../../../../../models/extraction";
import { Camera, Clock } from "lucide-react";

type SessionDocument = AuditSession["documents"][number];

interface VideoCitationCreatorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: SessionDocument;
  signedUrl: string;
  videoDocument?: VideoDocument;
  onCitationCreate: (timestamp: number, description?: string) => Promise<void>;
}

function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface KeyMomentItemProps {
  moment: VideoKeyMoment;
  onSeek: (timestampSeconds: number) => void;
}

function KeyMomentItem({ moment, onSeek }: KeyMomentItemProps) {
  return (
    <div
      className="flex items-start gap-2 py-1 px-2 rounded hover:bg-stone-100 cursor-pointer"
      onClick={() => onSeek(moment.timestampSeconds)}
    >
      <button
        type="button"
        className="h-5 px-1.5 text-xs font-mono bg-stone-100 hover:bg-stone-200 rounded border border-stone-200 flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onSeek(moment.timestampSeconds);
        }}
      >
        {formatTimestamp(moment.timestampSeconds)}
      </button>
      <span className="text-xs text-stone-600 flex-1 line-clamp-2">
        {moment.actionDescription}
      </span>
    </div>
  );
}

interface ChapterCardProps {
  chapter: VideoChapter;
  onSeek: (timestampSeconds: number) => void;
}

function ChapterCard({ chapter, onSeek }: ChapterCardProps) {
  return (
    <Card className="transition-colors hover:bg-stone-50/50">
      <CardContent className="p-2">
        <div
          className="flex items-center gap-2 mb-1 cursor-pointer"
          onClick={() => onSeek(chapter.startSeconds)}
        >
          <button
            type="button"
            className="h-5 px-1.5 text-xs font-mono bg-stone-100 hover:bg-stone-200 rounded border border-stone-200"
            onClick={(e) => {
              e.stopPropagation();
              onSeek(chapter.startSeconds);
            }}
          >
            {formatTimestamp(chapter.startSeconds)}
          </button>
          <span className="font-medium text-xs truncate">{chapter.title}</span>
        </div>

        {chapter.keyMoments.length > 0 && (
          <div className="border-t border-stone-100 pt-1 mt-1">
            <div className="space-y-0.5">
              {chapter.keyMoments.map((moment, idx) => (
                <KeyMomentItem
                  key={`${chapter.id}-moment-${idx}`}
                  moment={moment}
                  onSeek={onSeek}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function VideoCitationCreatorModal({
  open,
  onOpenChange,
  document,
  signedUrl,
  videoDocument,
  onCitationCreate,
}: VideoCitationCreatorModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState(0);
  const [description, setDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Update current timestamp as video plays
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTimestamp(videoRef.current.currentTime);
    }
  }, []);

  // Seek to a specific timestamp
  const handleSeekToTimestamp = useCallback((timestampSeconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timestampSeconds;
      setCurrentTimestamp(timestampSeconds);
    }
  }, []);

  // Handle citation creation
  const handleGrabFrame = async () => {
    const timestamp = videoRef.current?.currentTime ?? currentTimestamp;
    setIsCreating(true);
    try {
      await onCitationCreate(timestamp, description || undefined);
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating citation:", error);
    } finally {
      setIsCreating(false);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setCurrentTimestamp(0);
      setDescription("");
    }
  }, [open]);

  // Count total key moments across all chapters
  const totalKeyMoments =
    videoDocument?.elements.reduce(
      (sum, chapter) => sum + chapter.keyMoments.length,
      0,
    ) ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[900px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Video Citation</DialogTitle>
        </DialogHeader>

        <div className="text-sm text-stone-500 mb-2">
          {document.name || document.fetchableDocument?.fileName || "Video"}
        </div>

        <div className="flex gap-4 flex-1 min-h-0">
          {/* Left panel: Video player and controls */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Video player */}
            <div className="bg-black rounded-lg overflow-hidden mb-4">
              <video
                ref={videoRef}
                src={signedUrl}
                controls
                className="w-full max-h-[350px] object-contain"
                onTimeUpdate={handleTimeUpdate}
              >
                <track kind="captions" />
              </video>
            </div>

            {/* Current timestamp and grab frame */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border">
                <Clock className="h-5 w-5 text-stone-500" />
                <div>
                  <div className="text-xs text-stone-500">Current Position</div>
                  <div className="font-mono text-lg font-semibold">
                    {formatTimestamp(currentTimestamp)}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  placeholder="Describe what this moment shows..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <Button
                className="w-full"
                onClick={handleGrabFrame}
                disabled={isCreating}
              >
                <Camera className="h-4 w-4 mr-2" />
                {isCreating ? "Creating Citation..." : "Grab Frame as Citation"}
              </Button>
            </div>
          </div>

          {/* Right panel: Chapters with key moments (if available) */}
          {videoDocument?.elements && videoDocument.elements.length > 0 && (
            <div className="w-72 flex flex-col min-h-0 border-l pl-4">
              <h3 className="text-sm font-semibold mb-3">
                Chapters ({videoDocument.elements.length})
                {totalKeyMoments > 0 && (
                  <span className="font-normal text-stone-500">
                    {" "}
                    · {totalKeyMoments} moments
                  </span>
                )}
              </h3>
              <ScrollArea className="flex-1">
                <div className="space-y-2 pr-2">
                  {videoDocument.elements.map((chapter) => (
                    <ChapterCard
                      key={chapter.id}
                      chapter={chapter}
                      onSeek={handleSeekToTimestamp}
                    />
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
